import dayjs from "dayjs";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useMemo } from "react";
import { SvcType } from "@/lib/utils";
import { normalizeKhmerPhoneNumber } from "@/utils/phone";
import { OrderItem } from "@/components/checkout/PaymentSummaryCard";
import { useBookingKey } from "./useBookingKey";
import { useGenerateBookingToken } from "./useBookingCheckout";
import { CheckoutCoupon } from "@/types/coupon";
import { CheckoutRoomItem } from "@/components/main-services/hotels/checkout/hotel.checkout.type";
import { isRentalTempBookingSnapshot } from "@/components/main-services/rentals/checkout/rental.checkout.type";
import { isTourActTempBookingSnapshot } from "@/components/main-services/tour-act/checkout/tour.checkout.type";
import { isSpaMsgTempBookingSnapshot } from "@/components/main-services/spa-msg/checkout/spa.checkout.type";
import {
  tourActCheckoutPayloadAtom,
  tourActSelectionAtom,
} from "@/stores/tour-act-checkout";
import {
  spaCheckoutPayloadAtom,
  spaTicketSelectionAtom,
} from "@/stores/spa-checkout";
import {
  hotelCheckoutPayloadAtom,
  hotelRoomSelectionAtom,
} from "@/stores/hotel-checkout";
import {
  rentalCheckoutPayloadAtom,
  rentalVehicleSelectionAtom,
} from "@/stores/rental-checkout";
import {
  isHotelTempBookingSnapshot,
  toOrderSummaryItems,
  getDurationLabel,
} from "@/components/main-services/hotels/checkout/hotelCheckout.helpers";
import {
  hotelCheckoutConfig,
  rentalCheckoutConfig,
  spaMsgCheckoutConfig,
  tourActCheckoutConfig,
} from "./useCheckoutSessionGuard";
import type {
  CompletedBookingSession,
  HotelTempBookingSnapshot,
  PaymentOption,
  RentalTempBookingSnapshot,
  SpaMsgTempBookingSnapshot,
  TourActTempBookingSnapshot,
} from "@/types/booking/checkout";

export type BookingKeyContext = {
  svcType: string;
  recordId: string;
  start: string;
  end: string;
  qty: number;

  adults?: number;
  children?: number;
  selectedQtyByRoomId?: Record<number, number>;

  tickets?: {
    priceType: string;
    price: string;
    salePrice: string;
    qty: string;
  }[];
};

export const buildBookingContextKey = (context: BookingKeyContext) => {
  if (!context.recordId || !context.start || !context.end || context.qty <= 0) {
    return null;
  }

  return [context.recordId, context.start, context.end, context.qty].join(":");
};

export const isCompletedBookingSession = (
  value: unknown,
): value is CompletedBookingSession => {
  if (!value || typeof value !== "object") return false;
  const session = value as Partial<CompletedBookingSession>;
  return session.success === true && session.code === "BOOKING_SUCCESS";
};

export type CheckoutPayload = {
  // share
  uniqueId?: string;
  start: string;
  end: string;
  totalAmount: number;
  totalBaseAmount: number;
  guest?: {
    firstName: string;
    lastName: string;
    nationalityCode: string;
    nationalityId?: number;
    email: string;
    phone: string;
    countryCode: string;
    note: string;
  };
  appliedCoupon?: CheckoutCoupon | null;
  couponToken?: string | null;
  trxId?: string | null;
  updatedAt: number;
  bookingSessionId: string;

  // rental
  rentalId?: string;
  qty?: number;
  vehicles?: {
    id: string;
    name: string;
    price: number;
    salePrice: number;
    days: number;
    qty: number;
  }[];

  // hotel
  hotelId?: string;
  rooms?: CheckoutRoomItem[];
  adults?: number;
  children?: number;
  ttlSvcInDay?: number;
  ttlSvcInNight?: number;
  stayNights?: number;

  // tour_act
  activityId?: string;
  date?: string;
  meetingPoint?: string;
  activitys?: {
    id: string;
    name: string;
    priceType: string;
    price: number;
    salePrice: number;
    qty: number;
  }[];
  packages?: {
    id: string;
    title: string;
    duration: string;
    departureTime: string;
    quantity: number;
    price: number;
    originalPrice: number;
  }[];

  // spa_msg
  spaId?: string;
  services?: {
    serviceName: string;
    qty: number;
    durationInMin: number;
    price: number;
    salePrice?: number;
  }[];
};

export type BookingPayload = {
  bookingSessionId: string;
  email: string;
  fullName: string;
  nationalityId: number;
  phone: string;
  countryCode: string;
  paymentOption: PaymentOption;
  note: string;
};

export const buildBookingPayload = (
  checkoutPayload: CheckoutPayload,
  paymentOption: PaymentOption,
): BookingPayload => {
  if (!checkoutPayload.guest) {
    throw new Error("Rental checkout payload is missing guest details.");
  }

  const { guest } = checkoutPayload;
  const fullName = `${guest.firstName} ${guest.lastName}`.trim();

  return {
    email: checkoutPayload.guest.email,
    fullName,
    nationalityId: checkoutPayload.guest.nationalityId || 0,
    phone: normalizeKhmerPhoneNumber(checkoutPayload.guest.phone) || "",
    countryCode: checkoutPayload.guest.countryCode.replace("+", ""),
    note: checkoutPayload.guest.note.trim(),
    bookingSessionId: checkoutPayload.bookingSessionId ?? "",
    paymentOption,
  };
};

type RotateKeyFn = ReturnType<typeof useBookingKey>["rotateKey"];
type GenerateBookingTokenFn = ReturnType<
  typeof useGenerateBookingToken
>["mutateAsync"];
export const SVC_TYPE_CONFIG = {
  hotel: {
    selectionAtom: hotelRoomSelectionAtom,
    checkoutPayloadAtom: hotelCheckoutPayloadAtom,
    checkoutConfig: hotelCheckoutConfig,
    notAvailableCode: "ROOM_NOT_AVAILABLE",
    toOrderItems: (payload: CheckoutPayload): OrderItem[] => {
      if (!payload.rooms?.length) return [];
      return toOrderSummaryItems(
        payload.rooms.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.qty,
          price: item.price,
          salePrice: item.salePrice,
        })),
        payload.stayNights,
      );
    },
    regenerateToken: async (
      snapshot: HotelTempBookingSnapshot,
      rotateKey: RotateKeyFn,
      generateBookingToken: GenerateBookingTokenFn,
    ) => {
      const key = rotateKey({
        recordId: String(snapshot.hotel.id),
        svcType: "hotel",
        start: snapshot.booking.start,
        end: snapshot.booking.end,
        adults: snapshot.booking.adults,
        children: snapshot.booking.children ?? 0,
        qty: snapshot.rooms.reduce((sum, room) => sum + room.qty, 0),
        selectedQtyByRoomId: Object.fromEntries(
          snapshot.rooms.map((room) => [room.roomId, room.qty]),
        ),
      });
      if (!key) return null;

      return generateBookingToken({
        svcType: "hotel",
        itemId: Number(snapshot.hotel.id),
        key,
        filter: {
          start: snapshot.booking.start,
          end: snapshot.booking.end,
          adults: snapshot.booking.adults,
          children: snapshot.booking.children ?? 0,
          room: snapshot.rooms.length,
        },
        rooms: snapshot.rooms.map((room) => ({
          id: room.roomId,
          qty: room.qty,
        })),
        isNewPayload: true,
      });
    },
  },
  rental: {
    selectionAtom: rentalVehicleSelectionAtom,
    checkoutPayloadAtom: rentalCheckoutPayloadAtom,
    checkoutConfig: rentalCheckoutConfig,
    notAvailableCode: "VEHICLE_NOT_AVAILABLE",
    toOrderItems: (payload: CheckoutPayload): OrderItem[] => {
      if (!payload.vehicles?.length) return [];
      return payload.vehicles.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.qty,
        durationLabel: getDurationLabel(item.days, "rental"),
        unitLabel: "Unit",
        price: item.price,
        salePrice: item.salePrice,
      }));
    },
    regenerateToken: async (
      snapshot: RentalTempBookingSnapshot,
      rotateKey: RotateKeyFn,
      generateBookingToken: GenerateBookingTokenFn,
    ) => {
      const key = rotateKey({
        svcType: "rental",
        recordId: String(snapshot.rental.id),
        qty: snapshot.rental.qty,
        start: snapshot.booking.start,
        end: snapshot.booking.end,
      });
      if (!key) return null;

      return generateBookingToken({
        svcType: "rental",
        itemId: Number(snapshot.rental.id),
        qty: String(snapshot.rental.qty),
        uniqueId: snapshot.rental.uniqueId,
        key,
        filter: {
          start: snapshot.booking.start,
          end: snapshot.booking.end,
        },
        isNewPayload: true,
      });
    },
  },
  tour_act: {
    selectionAtom: tourActSelectionAtom,
    checkoutPayloadAtom: tourActCheckoutPayloadAtom,
    checkoutConfig: tourActCheckoutConfig,
    notAvailableCode: "SERVICE_NOT_AVAILABLE",
    toOrderItems: (payload: CheckoutPayload): OrderItem[] => {
      if (!payload.packages?.length) return [];
      return payload.packages.map((item) => ({
        id: item.id,
        name: item.title,
        quantity: item.quantity,
        unitLabel: "Ticket",
        price: item.price,
        salePrice: item.originalPrice,
      }));
    },
    regenerateToken: async (
      snapshot: TourActTempBookingSnapshot,
      rotateKey: RotateKeyFn,
      generateBookingToken: GenerateBookingTokenFn,
    ) => {
      const totalQty = snapshot.tickets.reduce(
        (sum, t) => sum + Number(t.qty),
        0,
      );

      const rawStartDate =
        snapshot.booking.start || snapshot.booking.reservationDt;
      const startDate = dayjs(rawStartDate).format("YYYY-MM-DD");

      const key = rotateKey({
        svcType: "tour_act",
        recordId: String(snapshot.tour_act.id),
        start: startDate,
        end: startDate,
        qty: totalQty,
        tickets: snapshot.tickets.map((ticket) => ({
          priceType: ticket.priceType,
          price: String(ticket.price),
          salePrice: String(ticket.salePrice),
          qty: String(ticket.qty),
        })),
      });

      if (!key) return null;

      return generateBookingToken({
        svcType: "tour_act",
        itemId: Number(snapshot.tour_act.id),
        isNewPayload: true,
        key,
        filter: {
          start: startDate,
          end: startDate,
        },
        tickets: snapshot.tickets.map((ticket) => ({
          pricingId: ticket.pricingId,
          priceType: ticket.priceType,
          price: String(ticket.price),
          salePrice: String(ticket.salePrice),
          qty: String(ticket.qty),
        })),
      });
    },
  },
  spa_msg: {
    selectionAtom: spaTicketSelectionAtom,
    checkoutPayloadAtom: spaCheckoutPayloadAtom,
    checkoutConfig: spaMsgCheckoutConfig,
    notAvailableCode: "SPA_SMG_NOT_AVAILABLE",

    toOrderItems: (payload: CheckoutPayload): OrderItem[] => {
      if (!payload.services?.length) {
        return [];
      }

      return payload.services.map((item, index) => ({
        id: `${item.serviceName}-${index}`,
        name: item.serviceName,
        quantity: Number(item.qty),
        durationLabel: `${Number(item.durationInMin)} min`,
        unitLabel: "Ticket",
        price: Number(item.price) * Number(item.qty),
        salePrice: Number(item.salePrice ?? item.price) * Number(item.qty),
      }));
    },

    regenerateToken: async (
      snapshot: SpaMsgTempBookingSnapshot,
      rotateKey: RotateKeyFn,
      generateBookingToken: GenerateBookingTokenFn,
    ) => {
      const services = snapshot.services ?? [];

      if (!services.length) {
        return null;
      }

      const totalQty = services.reduce(
        (sum, service) => sum + Number(service.qty),
        0,
      );

      const [startDate, startTime] = snapshot.booking.start.split(" ");

      const key = rotateKey({
        svcType: "spa_msg",
        recordId: String(snapshot.spa_msg.id),
        start: startDate,
        end: startDate,
        qty: totalQty,
      });

      if (!key) {
        return null;
      }

      return generateBookingToken({
        svcType: "spa_msg",
        itemId: snapshot.spa_msg.id,
        isNewPayload: "true",
        key,
        filter: {
          start: startDate,
          time: startTime,
        },
        services: services.map((service) => ({
          serviceName: service.serviceName,
          qty: Number(service.qty),
        })),
      });
    },
  },
} satisfies Partial<
  Record<
    string,
    {
      selectionAtom: unknown;
      checkoutPayloadAtom: unknown;
      checkoutConfig: unknown;
      notAvailableCode: string;
      toOrderItems: (payload: any) => OrderItem[];
      regenerateToken: (
        snapshot: any,
        rotateKey: any,
        generateBookingToken: any,
      ) => Promise<any>;
    }
  >
>;

export const BOOKING_SESSION_SVC_TYPE_CONFIG = {
  hotel: {
    isSnapshot: isHotelTempBookingSnapshot,
    getEntityId: (snapshot: HotelTempBookingSnapshot) =>
      String(snapshot.hotel.id),
  },
  rental: {
    isSnapshot: isRentalTempBookingSnapshot,
    getEntityId: (snapshot: RentalTempBookingSnapshot) =>
      String(snapshot.rental.id),
  },
  tour_act: {
    isSnapshot: isTourActTempBookingSnapshot,
    getEntityId: (snapshot: TourActTempBookingSnapshot) =>
      String(snapshot.tour_act.id),
  },
  spa_msg: {
    isSnapshot: isSpaMsgTempBookingSnapshot,
    getEntityId: (snapshot: SpaMsgTempBookingSnapshot) =>
      String(snapshot.spa_msg.id),
  },
} satisfies Partial<
  Record<
    SvcType,
    {
      isSnapshot: (raw: unknown) => boolean;
      getEntityId: (snapshot: any) => string;
    }
  >
>;

// =============
export const buildFindSimilarUrl = (
  searchParams: ReadonlyURLSearchParams,
  svcType?: string,
  overrides?: Record<string, string | undefined>,
) => {
  const params = new URLSearchParams();

  const setParam = (key: string) => {
    const value = overrides?.[key] ?? searchParams.get(key);

    if (value) {
      params.set(key, value);
    }
  };

  // Common
  setParam("locationId");
  setParam("start");
  setParam("end");

  // Hotel
  if (svcType === "hotel") {
    setParam("adults");
    setParam("children");
    setParam("rooms");
  }

  // Rental
  if (svcType === "rental") {
    setParam("vehicleType");
    setParam("pickupType");
  }

  if (svcType) {
    params.set("svcType", svcType);
  }

  return `/similar?${params.toString()}`;
};

// fallback url

export const buildHotelDetailFallbackUrl = (
  hotelId: string,
  searchParams: URLSearchParams,
) => {
  const query = new URLSearchParams(searchParams.toString());
  query.delete("bkt");
  query.delete("bookingSessionId");
  query.delete("service");
  query.delete("hotelId");
  const queryString = query.toString();
  return `/hotels/${hotelId}${queryString ? `?${queryString}` : ""}`;
};

export const useHotelDetailFallbackUrl = (
  hotelId: string,
  searchParams: URLSearchParams,
) => {
  return useMemo(
    () => buildHotelDetailFallbackUrl(hotelId, searchParams),
    [hotelId, searchParams],
  );
};

export const buildRentalDetailFallbackUrl = (
  rentalId: string,
  searchParams: URLSearchParams,
) => {
  const query = new URLSearchParams(searchParams.toString());
  query.delete("bkt");
  query.delete("bookingSessionId");
  query.delete("service");
  query.delete("rentalId");
  return `/rentals/${rentalId}${query.size ? `?${query.toString()}` : ""}`;
};

export const useRentalDetailFallbackUrl = (
  rentalId: string,
  searchParams: URLSearchParams,
) => {
  return useMemo(
    () => buildRentalDetailFallbackUrl(rentalId, searchParams),
    [rentalId, searchParams],
  );
};

export const buildSpaMassageDetailFallbackUrl = (
  rentalId: string,
  searchParams: URLSearchParams,
) => {
  const query = new URLSearchParams(searchParams.toString());
  query.delete("bkt");
  query.delete("bookingSessionId");
  query.delete("service");
  query.delete("activityId");
  return `/spa-msg/${rentalId}${query.size ? `?${query.toString()}` : ""}`;
};

export const useSpaMassageDetailFallbackUrl = (
  rentalId: string,
  searchParams: URLSearchParams,
) => {
  return useMemo(
    () => buildSpaMassageDetailFallbackUrl(rentalId, searchParams),
    [rentalId, searchParams],
  );
};

export const buildTourActDetailFallbackUrl = (
  rentalId: string,
  searchParams: URLSearchParams,
) => {
  const query = new URLSearchParams(searchParams.toString());
  query.delete("bkt");
  query.delete("bookingSessionId");
  query.delete("service");
  query.delete("activityId");
  return `/tour-act/${rentalId}${query.size ? `?${query.toString()}` : ""}`;
};

export const useTourActDetailFallbackUrl = (
  rentalId: string,
  searchParams: URLSearchParams,
) => {
  return useMemo(
    () => buildTourActDetailFallbackUrl(rentalId, searchParams),
    [rentalId, searchParams],
  );
};
